import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  BlocksTooltip,
  BlocksViewGraph,
  BlocksViewProperties,
  Controller,
  InteractionMode,
  LayoutData,
  LayoutType,
} from '../types';
import { PopoverAtPoint } from '@ardoq/popovers';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import {
  canvasResolvedImageReady,
  svgImageReady,
} from 'tabview/canvasRendering/canvasResolvedImages';
import {
  ViewCanvas,
  ZoomableViewLegendContainer,
  ZoomContainer,
} from 'tabview/relationshipDiagrams/atoms';
import {
  applyLayout,
  resetLayout,
  restoreLayout,
  fixupLayout,
  stripLayout,
} from '../layout/layout';
import { getLayoutData } from '../layout/util';
import { parseArrangements } from 'tabview/assistantBlocks/arrangementRecipe';
import { layOutWithArrangements } from '../layout/arrangementLayout';
import { isEqual } from 'lodash';
import { ViewContainer } from 'tabview/ViewContainer';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { createController } from './controller';
import { createAndUpdateViewContext, updateController } from './viewContext';
import { useEffectOnce } from '@ardoq/hooks';
import { ZoomControls } from 'atomicComponents/Zoomable/ZoomControls';
import { zoomToFitOnClick } from './viewpointInteractions';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { clearGraphSelection } from '@ardoq/component-selection';
import { getDefaultTooltipContent } from './tooltips';

const BlocksView = ({
  graph,
  arrangedLayout,
  viewSettings,
  viewInstanceId,
  isViewEmpty,
  performanceTrackingId,
  uiPropsAndCallbacks: { legendProps },
  i18nLocale,
  isPresentationMode,
  hideZoomControls,
  getTooltipContent = getDefaultTooltipContent,
  commands,
}: BlocksViewProperties) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const controllerRef = useRef<Controller | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const legendHeightOffset = useViewLegendSubscription();
  const layoutDataRef = useRef<LayoutData | null>(null);

  const graphRef = useRef<BlocksViewGraph | null>(null);
  const viewSettingsRef = useRef(viewSettings);
  viewSettingsRef.current = viewSettings;
  const arrangedLayoutRef = useRef(arrangedLayout);
  arrangedLayoutRef.current = arrangedLayout;

  const [tooltip, setTooltip] = useState<BlocksTooltip | null>(null);

  const handleTooltipClose = useCallback(() => {
    controllerRef.current?.cancelTooltip();
  }, []);
  const handleTooltipMouseEnter = useCallback(() => {
    controllerRef.current?.onTooltipMouseEnter();
  }, []);
  const handleTooltipMouseLeave = useCallback((event: ReactMouseEvent) => {
    controllerRef.current?.onTooltipMouseLeave(event);
  }, []);

  const { linksAggregation, isLegendActive } = viewSettings;

  useEffectOnce(() => {
    const rerender = () => controllerRef.current?.renderCanvas();
    const subscriptions = [canvasResolvedImageReady, svgImageReady].map(
      rerenderAction => subscribeToAction(rerenderAction, rerender)
    );
    subscriptions.push(
      subscribeToAction(clearGraphSelection, () => {
        controllerRef.current?.selection.clear();
        rerender();
      })
    );
    return () => {
      clearSubscriptions(subscriptions);
      if (controllerRef.current) {
        controllerRef.current.detach();
      }
    };
  });

  useEffect(() => {
    // #region initialize from view settings
    layoutDataRef.current = viewSettings.layoutData;

    createAndUpdateViewContext({
      controllerRef,
      containerRef,
      isLegendActive: viewSettings.isLegendActive,
      i18nLocale,
      viewInstanceId,
      commands,
    });
    // #endregion
  }, [viewSettings, i18nLocale, viewInstanceId, commands]);

  // This is a hack around the useEffect dependencies: isLegendActive is not
  // supposed to trigger the controller update, but it is included into it.
  // It should be dealt with during the architectural refactoring phase
  const isLegendActiveRef = useRef(viewSettings.isLegendActive);
  isLegendActiveRef.current = isLegendActive;

  const i18nLocaleRef = useRef(i18nLocale);
  i18nLocaleRef.current = i18nLocale;

  useEffect(() => {
    // #region initialize controller and load layout data
    const canvas = canvasRef.current;
    if (graph) {
      if (controllerRef.current) {
        controllerRef.current.detach();
      }

      if (canvas) {
        const onAppRefresh = () =>
          updateController({
            controllerRef,
            containerRef,
            isLegendActive: isLegendActiveRef.current,
            i18nLocale: i18nLocaleRef.current,
            viewInstanceId,
            commands,
          });
        const newController = createController({
          canvas,
          graph,
          viewInstanceId,
          appRefresh: onAppRefresh,
          linksAggregation: linksAggregation ?? 'separate',
          i18nLocale,
          isPresentationMode,
          setTooltip,
          getTooltipContent,
          onRenderComplete: () => {
            if (performanceTrackingId) {
              commands.recordRenderComplete(performanceTrackingId);
            }
          },
          commands,
          getArrangements: () =>
            parseArrangements(viewSettingsRef.current.arrangements),
          getCoveredContainerIds: () =>
            arrangedLayoutRef.current?.coveredContainerIds ?? [],
        });

        controllerRef.current = newController;
      }

      const restoredLayout = layoutDataRef.current;
      const arranged = arrangedLayoutRef.current;
      if (arranged) {
        if (layOutWithArrangements(graph.root, restoredLayout, arranged)) {
          commands.saveArrangedLayout(getLayoutData(graph.root.children));
        }
      } else if (!restoredLayout || !Object.keys(restoredLayout).length) {
        /* have no layout info to restore */

        resetLayout(graph.root, LayoutType.Smart, false);
      } else {
        /* have layout info to restore */

        const shouldRestore = !isEqual(
          restoredLayout,
          getLayoutData(graph.root.children)
        );

        if (shouldRestore) {
          if (!restoreLayout(graph.root, restoredLayout)) {
            fixupLayout(graph.root);
          }

          stripLayout(graph.root);
        }
      }

      graphRef.current = graph;
      applyLayout(graph, linksAggregation);

      const controller = controllerRef.current;

      const interactionMode = isPresentationMode
        ? InteractionMode.Presentation
        : InteractionMode.Viewpoint;

      controller?.fit(graph.root.bounds, false);
      controller?.setInteractionMode(interactionMode);
      controller?.renderCanvas();

      createAndUpdateViewContext({
        controllerRef,
        containerRef,
        isLegendActive: isLegendActiveRef.current,
        i18nLocale: i18nLocaleRef.current,
        viewInstanceId,
        commands,
      });
    }
    // #endregion
  }, [
    graph,
    viewInstanceId,
    linksAggregation,
    performanceTrackingId,
    isPresentationMode,
    i18nLocale,
    getTooltipContent,
    commands,
  ]);

  const zoomCenter = () => {
    if (!graph || !controllerRef.current) {
      return;
    }
    zoomToFitOnClick(controllerRef.current)();
  };

  return (
    <ViewContainer ref={containerRef}>
      {!isViewEmpty ? (
        <>
          <ViewCanvas
            ref={canvasRef}
            tabIndex={0}
            data-canvas-hit-test={viewInstanceId}
          />
          {isPresentationMode && !hideZoomControls && (
            <ZoomContainer>
              <ZoomControls
                zoomIn={() => controllerRef.current?.zoomIn()}
                zoomOut={() => controllerRef.current?.zoomOut()}
                zoomCenter={zoomCenter}
                i18nLocale={i18nLocale}
              />
            </ZoomContainer>
          )}

          <ZoomableViewLegendContainer
            heightOffset={legendHeightOffset || 0}
            visible={viewSettings.isLegendActive}
          >
            <ViewLegend
              componentTypes={[]}
              referenceTypes={[]}
              {...legendProps}
              labels={getViewLegendLabels(i18nLocale)}
            />
          </ZoomableViewLegendContainer>
        </>
      ) : null}
      {tooltip ? (
        <PopoverAtPoint
          point={tooltip.point}
          isOpen
          acceptsMouse
          placement={{ side: 'bottom', overflow: 'center' }}
          onClose={handleTooltipClose}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {tooltip.content}
        </PopoverAtPoint>
      ) : null}
    </ViewContainer>
  );
};
export { BlocksView };
